<template>
  <div class="old-koyou-keiyakus edit-item add-row" v-if="editingData">
    <div class="head"><b>内容変更の対象の<br>雇用契約</b></div>

    <div class="data flex flex-align-center">
      <template v-if="editingData.old_koyou_keiyakus.length < 1">
        <p>無し</p>
        <div class="item row-button" v-if="!disabled">
          <i class="fa-solid fa-circle-plus" @click="addOldKoyouKeiyaku()"></i>
        </div>

        <p class="input-error nothing" v-if="editingData.errors['old_koyou_keiyakus'] && !disabled">{{
          editingData.errors['old_koyou_keiyakus'][0] }}</p>
      </template>

      <template v-else>
        <div class="old-koyou-keiyaku flex flex-align-center" v-for="(item, index) in editingData.old_koyou_keiyakus"
          :key="index">
          <div class="item index">
            <b v-if="index + 1 === 1">変更対象（主）</b>
            <b v-else>変更対象 {{ (index + 1) }}</b>
          </div>

          <div class="item selecter-wrapper">
            <select-box :options="convertedList" v-model="item.old_koyou_keiyaku_id"
              :error="getError(editingData.old_koyou_keiyakus![index], 'old_koyou_keiyakus')" :disabled="disabled">
            </select-box>
            <p v-if="item.is_main">※この雇用契約を元に明示書等の複製が行われます</p>
          </div>

          <div class="item row-button" v-if="!disabled">
            <template v-if="index > 0">
              <i class="fa-solid fa-delete-left"
                @click="removeOldKoyouKeiyaku(editingData!.old_koyou_keiyakus, index)"></i>
            </template>

            <template v-if="convertedList.length > 0">
              <i class="fa-solid fa-circle-plus" @click="addOldKoyouKeiyaku()"></i>
            </template>
          </div>

        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { useForm } from '@/composable/form/form';
import { KoyouKeiyakTsuiseki } from '@/types/KoyouKeiyaku';
import { KoyouKeiyakuDetail } from '@/types/KoyouKeiyakuDetail';
import { Ref, defineComponent, onMounted, ref } from 'vue';
import { useTableRow } from "@/composable/common/tablerow";
import OptionConst from '@/const/Option';
import { useKoyouKeiyakuSearch } from '@/composable/koyoukeiyaku/search';
import { BaseOptionWithoutCode } from '@/types/Option';
import { DialogService } from '@/service/DialogService';

export default defineComponent({
  props: {
    editingData: {
      type: Object as () => KoyouKeiyakuDetail | null,
      require: true,
      default: null
    },
    disabled: {
      type: Object as () => boolean,
      default: false
    }
  },
  emits: {
    error: (result: boolean) => true,
  },
  setup(props, context) {
    const { getError } = useForm();
    const { addRowNoRef, removeTableRow } = useTableRow();
    const { findAllChangeableKoyouKeiyaku, changeableKeiyakuList } = useKoyouKeiyakuSearch()

    const convertedList: Ref<BaseOptionWithoutCode[]> = ref([])
    onMounted(async () => {
      if (!props.editingData?.user_id) {
        await DialogService.error({
          text: '内容変更対象のユーザーを特定できません。別の申請種別を選択してください'
        }).then(() => {
          context.emit('error', true)
        })
        return
      }
      await findAllChangeableKoyouKeiyaku(props.editingData.user_id)

      if (changeableKeiyakuList.value.length > 0) {
        await matchingList()
        await convertList()
      } else {
        // 変更可能な雇用契約がない場合は、変更前雇用契約として生成されたオブジェクトを破棄
        props.editingData.clearOldKoyouKeiyakus()
      }
    })

    // 行追加
    const addOldKoyouKeiyaku = () => {
      if (!props.editingData) {
        return
      }
      const row = new KoyouKeiyakTsuiseki();
      if (props.editingData.old_koyou_keiyakus.length < 1) {
        row.is_main = true
      }
      addRowNoRef(props.editingData.old_koyou_keiyakus, row);
    }

    const removeOldKoyouKeiyaku = async (targets: KoyouKeiyakTsuiseki[], index: number) => {
      const isMain = targets[index].is_main
      await removeTableRow(targets, index)
      // 削除対象が主となっている場合、先頭の契約を主とする
      if (isMain) {
        targets[0].is_main = true
      }
    }

    // 予め持っているold_koyou_keiyakusと取得した変更可能一覧のマッチングを行う
    const matchingList = async () => {
      if (!props.editingData) {
        return
      }

      let unmatched = false
      props.editingData.old_koyou_keiyakus.forEach((keiyaku, index) => {
        const find = changeableKeiyakuList.value.find((changeable =>
          changeable.koyou_keiyaku_id === keiyaku.old_koyou_keiyaku_id
        ))

        if (!find && props.editingData) {
          // アンマッチなものをold_koyou_keiaykusから削除（変更対象ではないためサーバーに送ってはいけない）
          props.editingData.old_koyou_keiyakus.splice(index)
          unmatched = true
        }
      })

      if (unmatched) {
        await DialogService.error({
          text: '内容変更対象に選択できない雇用契約がありました。「内容変更の対象の雇用契約」欄にて選び直してください。'
        })
      }
    }

    // 取得した雇用契約一覧をBaseOptionWithoutCodeの形式にする
    const convertList = async () => {
      convertedList.value = []
      changeableKeiyakuList.value.forEach(keiyaku => {
        const item: BaseOptionWithoutCode = (keiyaku.koyou_end_date === null) ? {
          val: keiyaku.koyou_keiyaku_id,
          label: '[' + keiyaku.koyou_keiyaku_id + '] ' + keiyaku.buka_name + ' (' + keiyaku.keiyaku_date + '〜' + OptionConst.KOYOU_KEIYAKU_MUKI_LABEL + ')'
        } : {
          val: keiyaku.koyou_keiyaku_id,
          label: '[' + keiyaku.koyou_keiyaku_id + '] ' + keiyaku.buka_name + ' (' + keiyaku.keiyaku_date + '〜' + keiyaku.koyou_end_date + ')'
        }
        convertedList.value.push(item)
      })
    }

    return {
      getError,
      removeTableRow,
      addOldKoyouKeiyaku,
      removeOldKoyouKeiyaku,
      OptionConst,
      convertedList
    }
  }
})
</script>

<style lang="scss" scoped>
@import '@scss/components/koyoukeiyaku.scss';
@import '@scss/components/koyoukeiyaku-edit.scss';

.old-koyou-keiyaku {
  padding-bottom: 20px;

  &:not(:first-of-type) {
    border-top: 1px solid $themeColor;
    padding-top: 20px;
  }

  &:last-of-type {
    padding-bottom: 10px;
  }

  .index {
    width: 100px;
  }

  .selecter-wrapper {
    margin-left: 20px;
    margin-right: 20px;
  }
  .input-error.nothing{
    width: 100%;
  }
}
</style>
